"use client";

import CdnImage from "@/components/CdnImage";

type SupportedSource = "predict" | "kalshi";

interface SourceConfig {
  logo: string;
  alt: string;
  gradient: string;
  defaultTitle: string;
  defaultCopy: string;
}

const CONFIG: Record<SupportedSource, SourceConfig> = {
  predict: {
    logo: "/logos/predictfun/Predict-Icon-PB.png",
    alt: "Predict.fun",
    gradient: "from-[#6366F1]/20 to-[#6366F1]/5",
    defaultTitle: "No Predict.fun markets",
    defaultCopy: "There are no Predict.fun markets in this category. Try another category or source!",
  },
  kalshi: {
    logo: "/logos/kalshi/kalshi-logo.svg",
    alt: "Kalshi",
    gradient: "from-[#09C285]/20 to-[#09C285]/5",
    defaultTitle: "No Kalshi markets",
    defaultCopy: "There are no Kalshi markets in this category. Try another category or source!",
  },
};

interface SourceEmptyStateProps {
  source: SupportedSource;
  /** When set, replaces the title with `No results for "<query>"`. */
  searchQuery?: string;
  /** Whether to render the search-specific copy (vs the no-markets-here copy). */
  searchActive?: boolean;
  onSwitchToPolymarket: () => void;
}

/**
 * Centered "no markets" empty state with a source logo + CTA to switch
 * back to Polymarket. The Predict and Kalshi versions used to live
 * inline in MarketsPageContent as duplicated 20-line blocks; the only
 * differences were the logo, gradient tint and copy. Collapsed here.
 */
export function SourceEmptyState({
  source,
  searchQuery,
  searchActive = false,
  onSwitchToPolymarket,
}: SourceEmptyStateProps) {
  const cfg = CONFIG[source];
  const title = searchActive && searchQuery ? `No results for "${searchQuery}"` : cfg.defaultTitle;
  const copy = searchActive ? "Try a different search term." : cfg.defaultCopy;

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className={`w-20 h-20 rounded-[5px] bg-gradient-to-br ${cfg.gradient} flex items-center justify-center mb-6`}>
        <CdnImage src={cfg.logo} alt={cfg.alt} width={40} height={40} className="w-10 h-10" />
      </div>
      <h3 className="text-xl font-monument font-bold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-gray-500 dark:text-gray-400 font-satoshi text-center max-w-md mb-6">
        {copy}
      </p>
      <button
        onClick={onSwitchToPolymarket}
        className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-[5px] font-inter-tight font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
      >
        View Polymarket
      </button>
    </div>
  );
}
